import React, { useState, useEffect } from 'react';
import independentCommissionsService from '../../services/independentCommissionsService';
import { independentProfileService } from '../../services/independent';
import './IndependentCommissionsList.css';

const IndependentCommissionsList = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await independentCommissionsService.getAll();
      setCommissions(data);
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this commission record?')) {
      return;
    }

    try {
      await independentCommissionsService.delete(id);
      setCommissions(commissions.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting commission:', err);
      alert('Failed to delete commission');
    }
  };

  const handleEdit = (commission) => {
    setEditingCommission(commission);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCommission(null);
    fetchCommissions();
  };

  const filteredCommissions = commissions.filter(commission => {
    const matchesFilter = filter === 'all' || commission.status === filter;
    const matchesSearch = 
      commission.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.reservation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commission.destination?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      received: 'status-badge status-received',
      disputed: 'status-badge status-disputed'
    };
    return <span className={statusClasses[status] || 'status-badge'}>{status}</span>;
  };

  const getCommissionTypeLabel = (type) => {
    const labels = {
      air: 'Air',
      hotel: 'Hotel',
      cruise: 'Cruise',
      tour: 'Tour',
      car: 'Car Rental',
      insurance: 'Insurance',
      other: 'Other'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="commissions-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading commissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="commissions-container">
      <div className="commissions-header">
        <h1>Commission Tracker</h1>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingCommission(null);
            setShowForm(true);
          }}
        >
          + Add Commission
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="commissions-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="received">Received</option>
            <option value="disputed">Disputed</option>
          </select>
        </div>

        <div className="search-group">
          <input
            type="text"
            placeholder="Search by client, reservation, or destination..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredCommissions.length === 0 ? (
        <div className="empty-state">
          <p>No commissions found.</p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingCommission(null);
              setShowForm(true);
            }}
          >
            Add Your First Commission
          </button>
        </div>
      ) : (
        <div className="commissions-table-container">
          <table className="commissions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Type</th>
                <th>Destination</th>
                <th>Travel Dates</th>
                <th>Gross</th>
                <th>Net</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.map(commission => (
                <tr key={commission.id}>
                  <td>{formatDate(commission.created_at)}</td>
                  <td>
                    <div className="client-info">
                      <strong>{commission.client_name}</strong>
                      {commission.reservation_number && (
                        <small>Res: {commission.reservation_number}</small>
                      )}
                    </div>
                  </td>
                  <td>{getCommissionTypeLabel(commission.commission_type)}</td>
                  <td>{commission.destination || 'N/A'}</td>
                  <td>
                    {commission.travel_dates_start ? (
                      <>
                        {formatDate(commission.travel_dates_start)}
                        {commission.travel_dates_end && (
                          <> - {formatDate(commission.travel_dates_end)}</>
                        )}
                      </>
                    ) : 'N/A'}
                  </td>
                  <td>{formatCurrency(commission.gross_commission)}</td>
                  <td className="net-commission">{formatCurrency(commission.net_commission)}</td>
                  <td>{getStatusBadge(commission.status)}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon btn-edit"
                        onClick={() => handleEdit(commission)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(commission.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CommissionFormModal
          commission={editingCommission}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

// Commission Form Modal Component
const CommissionFormModal = ({ commission, onClose }) => {
  const [formData, setFormData] = useState({
    reservation_number: commission?.reservation_number || '',
    client_name: commission?.client_name || '',
    travel_dates_start: commission?.travel_dates_start || '',
    travel_dates_end: commission?.travel_dates_end || '',
    destination: commission?.destination || '',
    commission_type: commission?.commission_type || 'other',
    gross_commission: commission?.gross_commission || '',
    net_commission: commission?.net_commission || '',
    commission_percentage: commission?.commission_percentage || '',
    status: commission?.status || 'pending',
    notes: commission?.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch profile to get default commission percentage
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await independentProfileService.getProfile();
        // Only auto-fill if this is a new commission (not editing) and no percentage is set
        if (!commission && profile.commission_percentage) {
          setFormData(prev => ({
            ...prev,
            commission_percentage: profile.commission_percentage
          }));
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };
    fetchProfile();
  }, [commission]);

  // Auto-calculate net commission when gross commission or percentage changes
  useEffect(() => {
    const gross = parseFloat(formData.gross_commission) || 0;
    const percentage = parseFloat(formData.commission_percentage) || 0;
    
    if (gross > 0 && percentage > 0) {
      // Proper rounding that rounds .005 up instead of banker's rounding
      const value = (gross * percentage) / 100;
      const netCommission = (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);
      setFormData(prev => ({
        ...prev,
        net_commission: netCommission
      }));
    }
  }, [formData.gross_commission, formData.commission_percentage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.client_name || !formData.commission_type || !formData.gross_commission || !formData.net_commission) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      if (commission) {
        await independentCommissionsService.update(commission.id, formData);
      } else {
        await independentCommissionsService.create(formData);
      }
      onClose();
    } catch (err) {
      console.error('Error saving commission:', err);
      setError('Failed to save commission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content commission-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{commission ? 'Edit Commission' : 'Add Commission'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="commission-form">
          <div className="form-row">
            <div className="form-group">
              <label>Client Name *</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Reservation Number</label>
              <input
                type="text"
                value={formData.reservation_number}
                onChange={(e) => setFormData({ ...formData, reservation_number: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Commission Type *</label>
              <select
                value={formData.commission_type}
                onChange={(e) => setFormData({ ...formData, commission_type: e.target.value })}
                required
              >
                <option value="air">Air</option>
                <option value="hotel">Hotel</option>
                <option value="cruise">Cruise</option>
                <option value="tour">Tour</option>
                <option value="car">Car Rental</option>
                <option value="insurance">Insurance</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Destination</label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Travel Start Date</label>
              <input
                type="date"
                value={formData.travel_dates_start}
                onChange={(e) => setFormData({ ...formData, travel_dates_start: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Travel End Date</label>
              <input
                type="date"
                value={formData.travel_dates_end}
                onChange={(e) => setFormData({ ...formData, travel_dates_end: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gross Commission *</label>
              <input
                type="number"
                step="0.01"
                value={formData.gross_commission}
                onChange={(e) => setFormData({ ...formData, gross_commission: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div className="form-group">
              <label>Commission % *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.commission_percentage}
                onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                placeholder="0.00"
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Auto-filled from profile</small>
            </div>

            <div className="form-group">
              <label>Net Commission *</label>
              <input
                type="number"
                step="0.01"
                value={formData.net_commission}
                onChange={(e) => setFormData({ ...formData, net_commission: e.target.value })}
                placeholder="0.00"
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>Auto-calculated</small>
            </div>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="pending">Pending</option>
              <option value="received">Received</option>
              <option value="disputed">Disputed</option>
            </select>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : commission ? 'Update Commission' : 'Add Commission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IndependentCommissionsList;
